import { useCallback, useEffect, useState } from 'react'
import {
  SAVED_VIEW_VISIBILITY_LABELS,
  savedViewsApi,
  type SavedView,
  type SavedViewPage,
  type SavedViewState,
  type SavedViewVisibility,
} from '../api/savedViews'
import { Button, Callout, Card, Icon, Select, TextField, useConfirm } from './ui'
import './shared.css'

/**
 * F-063 (TASK-0317, REQ-242-001): barra delle viste salvate per un elenco. Non conosce i filtri della
 * pagina: la pagina le passa due funzioni — `getState()` (fotografa i filtri applicati adesso) e
 * `applyState()` (li riapplica) — cosi' si innesta senza riscrivere la pagina. Le viste Aziendali le
 * puo' creare solo chi ha il ruolo Manager: il server risponde 403 agli altri e il messaggio compare qui.
 */
interface Props {
  pageKey: SavedViewPage
  getState: () => SavedViewState
  applyState: (state: SavedViewState) => void
}

export function SavedViewsBar({ pageKey, getState, applyState }: Props) {
  const [views, setViews] = useState<SavedView[]>([])
  const [selectedId, setSelectedId] = useState('')
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const [visibility, setVisibility] = useState<SavedViewVisibility>('Privata')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const confirm = useConfirm()

  const reload = useCallback(async () => {
    try {
      const res = await savedViewsApi.list(pageKey)
      setViews(res.items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento delle viste')
    }
  }, [pageKey])

  useEffect(() => { void reload() }, [reload])

  const selected = views.find(v => String(v.id) === selectedId) ?? null

  function handleSelect(id: string) {
    setSelectedId(id)
    setError(null)
    const view = views.find(v => String(v.id) === id)
    if (view) applyState(view.state)
  }

  async function handleCreate() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const created = await savedViewsApi.create({ pageKey, name, visibility, state: getState() })
      await reload()
      setSelectedId(String(created.id))
      setNaming(false)
      setName('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setBusy(false)
    }
  }

  async function handleOverwrite() {
    if (!selected || busy) return
    setBusy(true)
    setError(null)
    try {
      await savedViewsApi.update(selected.id, {
        pageKey, name: selected.name, visibility: selected.visibility, state: getState(),
      })
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete() {
    if (!selected || busy) return
    const ok = await confirm({
      title: 'Elimina vista',
      message: `Eliminare la vista "${selected.name}"?`,
      confirmLabel: 'Elimina',
      destructive: true,
    })
    if (!ok) return
    setBusy(true)
    setError(null)
    try {
      await savedViewsApi.remove(selected.id)
      setSelectedId('')
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nell\'eliminazione')
    } finally {
      setBusy(false)
    }
  }

  const byVisibility = (v: SavedViewVisibility) => views.filter(x => x.visibility === v)

  return (
    <Card variant="outlined" padding="sm">
      <div className="ecrm-saved-views">
        <Icon name="bookmark" size={18} />
        <Select
          label="Viste salvate"
          labelHidden
          value={selectedId}
          onChange={e => handleSelect(e.target.value)}
        >
          <option value="">Viste salvate…</option>
          {(['Privata', 'Team', 'Aziendale'] as SavedViewVisibility[]).map(vis => (
            byVisibility(vis).length > 0 && (
              <optgroup key={vis} label={SAVED_VIEW_VISIBILITY_LABELS[vis]}>
                {byVisibility(vis).map(v => (
                  <option key={v.id} value={v.id}>{v.name}{v.isOwner ? '' : ' (di un collega)'}</option>
                ))}
              </optgroup>
            )
          ))}
        </Select>

        {selected?.isOwner && (
          <>
            <Button variant="outlined" size="sm" disabled={busy} onClick={() => void handleOverwrite()}>
              Aggiorna con i filtri attuali
            </Button>
            <Button variant="outlined" size="sm" disabled={busy} onClick={() => void handleDelete()}>
              Elimina
            </Button>
          </>
        )}

        {!naming && (
          <Button variant="outlined" size="sm" className="ecrm-saved-views__spacer"
            onClick={() => { setNaming(true); setError(null) }}>
            Salva come vista
          </Button>
        )}
      </div>

      {naming && (
        <div className="ecrm-saved-views">
          <TextField
            label="Nome della vista"
            labelHidden
            placeholder="Nome della vista"
            maxLength={100}
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <Select
            label="Visibilità"
            labelHidden
            value={visibility}
            onChange={e => setVisibility(e.target.value as SavedViewVisibility)}
          >
            {(Object.keys(SAVED_VIEW_VISIBILITY_LABELS) as SavedViewVisibility[]).map(v => (
              <option key={v} value={v}>{SAVED_VIEW_VISIBILITY_LABELS[v]}</option>
            ))}
          </Select>
          <Button variant="text" size="sm" onClick={() => setNaming(false)}>
            Annulla
          </Button>
          <Button variant="tonal" size="sm" disabled={busy || !name.trim()} onClick={() => void handleCreate()}>
            Salva
          </Button>
        </div>
      )}

      {error && <Callout tone="error">{error}</Callout>}
    </Card>
  )
}
